import { ref, computed } from 'vue'
import { COUNTRY_LABEL_FALLBACK, FULL_VIEWBOX, ZOOM_DUR, ZOOM_PAD } from '../helpers/DanmarksKortMap.constants.js'
import { getRegionLevelAttr, getCurrentLevelAttr, getCountryLevelAttr, statsForEntity, formatEntityLabel } from '../helpers/DanmarksKortMap.helpers.js'
import { useMapFocus } from './useMapFocus.js'

export function useMapNavigation(props, emit, caches, mapLevel, rootRef, deps) {
  const { animateViewBox, zoomTargetFor, applyColoringByAttr, applyColoringFromLegend, greyExcept, fetchRegionData, regionDataCache, clearHover } = deps

  const countryCrumb = () => ({ label: props.title || COUNTRY_LABEL_FALLBACK, level: 'country' })

  const crumbsForRegion = (regionKey) => [
    countryCrumb(),
    { label: regionKey, level: 'region' }
  ]

  const breadcrumb   = ref([])
  const activeGroup  = ref(null)
  const activeDetail = ref(null)
  const kommuneView  = ref(null)

  let selectionToken = 0

  const activeRegionGroups = computed(() => {
    if (!activeGroup.value) return []
    const groupAttr = getRegionLevelAttr(props.flow)
    if (!groupAttr) return []
    const set = new Set()
    caches.region?.group.get(activeGroup.value.key)?.forEach(p => set.add(p.dataset[groupAttr]))
    return [...set]
  })

  const currentLevelOptions = computed(() => {
    if (mapLevel.value === 'country') return [...(caches.region?.group.keys() ?? [])]
    if (mapLevel.value === 'region')  return activeRegionGroups.value
    return []
  })

  const regionStatsFrom = (data, key) => statsForEntity(data?.stats, key) ?? data?.region?.stats ?? null

  const emitState = () => emit('navigate', {
    level:   mapLevel.value,
    group:   activeGroup.value,
    detail:  activeDetail.value,
    options: currentLevelOptions.value
  })

  const { focusAfterZoom, focusRegionMarker } = useMapFocus(rootRef)

  const paintRegion = (regionKey) => {
    applyColoringByAttr(getRegionLevelAttr(props.flow) ?? 'name', props.groupData, regionKey)
  }

  const regionDetail = (key, stats) => ({ kind: 'region', label: key, region: key, stats })

  const enterRegion = (key) => {
    activeGroup.value  = { key }
    activeDetail.value = null
    breadcrumb.value   = [countryCrumb()]
    mapLevel.value     = 'region'
    clearHover()
  }

  const selectSundhedsraad = (key) => {
    const regionKey = activeGroup.value.key
    const data      = regionDataCache.get(regionKey)
    const srPaths   = caches.sr?.group.get(key) ?? []
    activeDetail.value = {
      kind: 'sundhedsraad', key, label: formatEntityLabel(key), region: regionKey,
      stats: statsForEntity(data?.sr?.stats, key),
      municipalities: srPaths.map(p => ({
        name:  p.dataset.name,
        stats: statsForEntity(data?.stats, p.dataset.name)
      }))
    }
    breadcrumb.value = crumbsForRegion(regionKey)
    mapLevel.value   = 'sundhedsraad'
    animateViewBox(zoomTargetFor('sr', key), ZOOM_DUR, focusAfterZoom)
    greyExcept('sr', key)
  }

  const selectMunicipality = (key, path) => {
    if (!path) return
    const regionKey = activeGroup.value.key
    const data      = regionDataCache.get(regionKey)
    const name      = path.dataset.name
    activeDetail.value = {
      kind: 'municipality', label: `${name} kommune`, region: regionKey,
      sr: path.dataset.sr, stats: statsForEntity(data?.stats, name)
    }
    breadcrumb.value = crumbsForRegion(regionKey)
    mapLevel.value   = 'municipality'
    const b = path.getBBox()
    animateViewBox(
      { x: b.x - ZOOM_PAD*4, y: b.y - ZOOM_PAD*4, w: b.width + ZOOM_PAD*8, h: b.height + ZOOM_PAD*8 },
      ZOOM_DUR, focusAfterZoom
    )
    greyExcept('name', name)
  }

  const paintSundhedsraadTab = () => {
    const detail = activeDetail.value
    if (mapLevel.value !== 'sundhedsraad' || detail?.kind !== 'sundhedsraad') {
      kommuneView.value = null
      return
    }
    const kommune = regionDataCache.get(detail.region)?.kommune
    if (props.srTab === 'kommuner' && kommune?.values?.length) {
      applyColoringFromLegend(caches.sr?.group.get(detail.key) ?? [], 'name', kommune.values, kommune.legend)
      kommuneView.value = kommune
      return
    }
    applyColoringByAttr('sr', props.groupData, detail.region)
    greyExcept('sr', detail.key)
    kommuneView.value = null
  }

  const selectFromRegion = (key, path) => {
    if (props.flow === 'region') return
    if (props.flow === 'sundhedsraad') selectSundhedsraad(key)
    else                               selectMunicipality(key, path)
    clearHover()
    emitState()
  }

  const selectFromCountry = async (key, token) => {
    enterRegion(key)
    animateViewBox(zoomTargetFor('region', key), ZOOM_DUR, focusAfterZoom)

    const isTerminal = props.flow === 'region'
    if (isTerminal) {
      activeDetail.value = regionDetail(key, null)
      greyExcept('region', key)
    }
    emitState()

    try {
      const data = await fetchRegionData(key)
      if (token !== selectionToken) return
      if (isTerminal) {
        activeDetail.value = regionDetail(key, regionStatsFrom(data, key))
        emitState()
      } else {
        paintRegion(key)
      }
    } catch (e) {
      if (token === selectionToken) emit('load-error', { groupKey: key, error: String(e) })
    }
  }

  const selectFromNeighbourRegion = async (path, token) => {
    const targetRegion = path.dataset.region
    if (!targetRegion) return

    enterRegion(targetRegion)

    try {
      await fetchRegionData(targetRegion)
      if (token !== selectionToken) return
      paintRegion(targetRegion)
    } catch (e) {
      if (token !== selectionToken) return
      emit('load-error', { groupKey: targetRegion, error: String(e) })
    }
    if (token !== selectionToken) return
    selectFromRegion(path.dataset[getRegionLevelAttr(props.flow)], path)
  }

  const selectGroup = async (key, path = null) => {
    const token = ++selectionToken
    if (mapLevel.value === 'country') {
      await selectFromCountry(key, token)
      return
    }
    if (mapLevel.value !== 'region') return

    if (path?.dataset.region && path.dataset.region !== activeGroup.value?.key) {
      await selectFromNeighbourRegion(path, token)
      return
    }
    selectFromRegion(key, path)
  }

  const goBack = () => {
    ++selectionToken
    const prev = breadcrumb.value[breadcrumb.value.length - 1]
    if (!prev) return
    breadcrumb.value = breadcrumb.value.slice(0, -1)

    if (prev.level === 'country') {
      const leftRegion   = activeGroup.value?.key
      mapLevel.value     = 'country'
      activeGroup.value  = null
      activeDetail.value = null
      animateViewBox(FULL_VIEWBOX, ZOOM_DUR, () => focusRegionMarker(leftRegion))
      applyColoringByAttr(getCountryLevelAttr(props.flow), props.groupData)
    } else if (prev.level === 'region') {
      mapLevel.value     = 'region'
      activeDetail.value = null
      animateViewBox(zoomTargetFor('region', activeGroup.value.key), ZOOM_DUR, focusAfterZoom)
      paintRegion(activeGroup.value.key)
    }
    emitState()
  }

  const selectRegionGroup = (key) => {
    const groupAttr = getRegionLevelAttr(props.flow)
    const path = caches.region.group.get(activeGroup.value.key)?.find(p => p.dataset[groupAttr] === key)
    if (path) selectGroup(key, path)
  }

  const onMapClick = (e) => {
    const path = e.target.closest('path[data-name]')
    if (!path) return
    const groupAttr = getCurrentLevelAttr(mapLevel.value, props.flow)
    if (!groupAttr) return
    selectGroup(path.dataset[groupAttr], path)
  }

  const selectByName = (name) => {
    if (mapLevel.value === 'country')     selectGroup(name)
    else if (mapLevel.value === 'region') selectRegionGroup(name)
  }

  return {
    breadcrumb, activeRegionGroups, kommuneView, paintSundhedsraadTab,
    emitState, selectGroup, goBack, selectByName, selectRegionGroup, onMapClick
  }
}
